import { useMemo } from 'react';
import { useApprovalSelection } from './hooks/useApprovalSelection';
import MainContainer from '@/components/containers/MainContainer';
import Bar from '@/components/elements/Bar';
import { Badge } from '@/components/ui/badge';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { OBJECT_TYPE_LABELS, STATUS_LABELS } from './constants';
import type { ApprovalRequest } from './types';
import { useApprovalDetail, useMyApprovals } from './hooks/useApprovals';
import ApprovalDetail from './components/ApprovalDetail';

const formatDate = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('cs-CZ');
};

function MyApprovals() {
  const [selectedId, setSelectedId] = useApprovalSelection();

  const { data, isLoading } = useMyApprovals();
  const { data: detail, isLoading: detailLoading } = useApprovalDetail(selectedId);

  const rows: ApprovalRequest[] = useMemo(() => data ?? [], [data]);

  return (
    <MainContainer>
      <div className="w-[380px] shrink-0 border-r border-neutral-150 flex flex-col min-h-0">
        <Bar text="Moje položky" />

        <div className="flex-1 min-h-0 overflow-y-auto">
          <SpinnerOverlay spinning={isLoading}>
            {rows.length === 0 ? (
              <div className="py-8 text-center text-sm text-neutral-400">Žádné žádosti</div>
            ) : (
              <ul>
                {rows.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(item.id)}
                      data-testid="my-approval-list-item"
                      className={cn(
                        'w-full text-left px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 transition-colors',
                        selectedId === item.id && 'bg-primary-50'
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-neutral-900 truncate">
                          {OBJECT_TYPE_LABELS[item.object_type]}
                        </span>
                        <Badge size="sm" variant="outline">
                          {STATUS_LABELS[item.status]}
                        </Badge>
                      </div>
                      <div className="text-xs text-neutral-500">{formatDate(item.created_on)}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </SpinnerOverlay>
        </div>
      </div>

      <ApprovalDetail request={detail} loading={!!selectedId && detailLoading} />
    </MainContainer>
  );
}

export default MyApprovals;
